// Toast wrapper around Base UI's headless Toast primitive.
//
// API surface stays compatible with the legacy XPrim.useToast() — a single function
// that accepts a string or an options object:
//   const { toast } = useToast();
//   toast('Saved');                              // info
//   toast({ title: 'Failed', type: 'error' });   // typed
//
// Visually identical to the previous bottom-right card style.

import React from 'react';
import { Toast as BaseToast } from '@base-ui/react/toast';
const { useToastManager } = BaseToast;

// Map legacy types to colors. 'success' kept as default (was the only legacy variant).
const COLOR = {
  info:    'var(--fg-1)',
  success: 'var(--ok)',
  warn:    'var(--warn)',
  error:   'var(--err)',
};

const VIEWPORT_STYLE = {
  position: 'fixed',
  bottom: 16,
  right: 16,
  zIndex: 200,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  pointerEvents: 'none',
};

const TOAST_STYLE = {
  pointerEvents: 'auto',
  background: 'var(--bg-1)',
  border: '1px solid var(--line-2)',
  borderRadius: 6,
  padding: '10px 14px',
  fontSize: 13,
  color: 'var(--fg-1)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
  minWidth: 220,
  maxWidth: 360,
};

// Internal: renders the actual toast list reading from useToastManager().
const ToastList = () => {
  // useToastManager() returns the manager; we render via BaseToast.Root with the toasts.
  // Per Base UI docs, the standard pattern is BaseToast.Provider + Viewport + Root collection.
  return (
    <BaseToast.Viewport style={VIEWPORT_STYLE}>
      <BaseToastSlot/>
    </BaseToast.Viewport>
  );
};

// Iterate the toast collection inside the viewport.
const BaseToastSlot = () => {
  const { toasts } = useToastManager();
  return (
    <>
      {(toasts || []).map((t) => (
        <BaseToast.Root key={t.id} toast={t} style={TOAST_STYLE}>
          <div className="row gap-2" style={{ alignItems: t.description ? 'flex-start' : 'center' }}>
            <span style={{
              color: COLOR[t.type] || COLOR.info,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 14,
              height: 14,
              flexShrink: 0,
              marginTop: t.description ? 2 : 0,
            }}>
              {t.type === 'error' ? (
                // Circled X — red. The previous neutral X looked too much like a "close" button.
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <circle cx="8" cy="8" r="6.5"/>
                  <path d="M5.5 5.5l5 5 M10.5 5.5l-5 5"/>
                </svg>
              ) : t.type === 'warn' ? (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 2l6.5 11.5h-13z M8 6.5v3.5 M8 12v.5"/>
                </svg>
              ) : t.type === 'success' ? (
                // Checkmark — reserved for success only. info now uses a neutral dot.
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8.5l3.5 3.5L13 5"/>
                </svg>
              ) : (
                // info / default — small filled dot. Doesn't imply success.
                <svg width="14" height="14" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="3" fill="currentColor"/>
                </svg>
              )}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {t.title && <BaseToast.Title style={{ margin: 0, fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>{t.title}</BaseToast.Title>}
              {t.description && <BaseToast.Description style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--fg-3)' }}>{t.description}</BaseToast.Description>}
            </div>
            <BaseToast.Close className="btn btn-sm btn-ghost btn-icon" aria-label="Dismiss" style={{ marginLeft: 4 }}>
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M3.5 3.5l9 9 M12.5 3.5l-9 9"/>
              </svg>
            </BaseToast.Close>
          </div>
        </BaseToast.Root>
      ))}
    </>
  );
};

// Provider wrapper — mounted once at app root.
export const ToastProvider = ({ children, timeout = 2200 }) => (
  <BaseToast.Provider timeout={timeout}>
    {children}
    <ToastList/>
  </BaseToast.Provider>
);

// Heuristic: when a plain string is passed and it clearly describes a failure,
// auto-promote to type='error'. Avoids needing every caller to convert to the
// object form when they just `toast(e.message)`.
const ERROR_RE = /failed|fail\b|error\b|不能|无法|失败|错误|cannot|denied|unauthor/i;
const SUCCESS_RE = /saved|created|enabled|disabled|connected|revoked|updated|copied|deleted|removed|done|成功|已保存|已创建|已启用|已禁用|已绑定|已撤销|已删除|已复制/i;
const classify = (str) => {
  if (ERROR_RE.test(str)) return 'error';
  if (SUCCESS_RE.test(str)) return 'success';
  return 'info';
};

// 去重：同一 (type|title) 在窗口内只弹一次。后端抖动/重启或多个轮询同刻失败时，
// 每个调用方各自 catch→toast 会刷出一墙相同的 "Internal Server Error"；这里把它们
// 合并成一条。不同文案不受影响。模块级（跨组件共享单例）。
const DEDUP_MS = 4000;
const _recentToasts = new Map(); // key -> last shown ts
const _shouldShow = (opts) => {
  const key = (opts.type || 'info') + '|' + (opts.title || '');
  const now = Date.now();
  const last = _recentToasts.get(key);
  _recentToasts.set(key, now);
  if (_recentToasts.size > 64) {
    for (const [k, ts] of _recentToasts) if (now - ts > DEDUP_MS) _recentToasts.delete(k);
  }
  return !(last && now - last < DEDUP_MS);
};

// Hook compatible with legacy useToast() — accepts string OR { title, description, type }.
export const useToast = () => {
  const manager = useToastManager();
  return {
    toast: (msg) => {
      if (msg == null) return;
      const opts = typeof msg === 'string'
        ? { title: msg, type: classify(msg) }
        : msg;
      if (!_shouldShow(opts)) return; // 抑制窗口内的重复
      try { manager.add(opts); } catch {}
    },
  };
};
